/* ============================================================================
   site-copy.js - public FAQ + legal CMS fetch / merge / render helpers
   ============================================================================ */

import { functions, isConfigured } from "./firebase-config.js?v=20261007m";
import { httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";
import { FAQ_SEED, FAQ_SEED_LANDING, FAQ_SEED_PORTAL } from "./faq-data.js?v=20261007m";
import { sanitizeFaqAnswerHtml, sanitizeLegalHtml } from "./sanitize-html.js?v=20261007m";

export { sanitizeFaqAnswerHtml, sanitizeLegalHtml };

function normalizeFaq(row) {
  if (!row?.question) return null;
  return {
    id: String(row.id || "").trim(),
    surface: row.surface === "landing" ? "landing" : "portal",
    section: String(row.section || "General").trim() || "General",
    question: String(row.question || "").trim(),
    answer_html: String(row.answer_html || row.answerHtml || "").trim(),
    sort: Number(row.sort) || 0,
    admin_added: Boolean(row.admin_added),
  };
}

const FAQ_CMS_CACHE_MS = 10 * 60 * 1000;

function faqCmsCacheKey(surface) {
  return `ccj_faq_cms_${surface || "all"}`;
}

function readFaqCmsCache(surface) {
  try {
    const raw = sessionStorage.getItem(faqCmsCacheKey(surface));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - Number(data.at || 0) > FAQ_CMS_CACHE_MS) return null;
    if (!data.payload || typeof data.payload !== "object") return null;
    return data.payload;
  } catch (_) {
    return null;
  }
}

function writeFaqCmsCache(surface, payload) {
  try {
    sessionStorage.setItem(
      faqCmsCacheKey(surface),
      JSON.stringify({ at: Date.now(), payload })
    );
  } catch (_) {}
}

/** Prefetch portal FAQ copy (nav-prefetch idle warm). */
export async function warmFaqCmsCache(surface = "portal") {
  const key = surface || "portal";
  if (readFaqCmsCache(key)) return readFaqCmsCache(key);
  const cms = await fetchFaqCms({ surface: key, force: true });
  writeFaqCmsCache(key, cms);
  return cms;
}

export async function fetchFaqCms({ surface, force = false } = {}) {
  const key = surface || "";
  if (!force && key) {
    const cached = readFaqCmsCache(key);
    if (cached) return cached;
  }

  if (!isConfigured || !functions) return { items: [], hidden_ids: [] };
  try {
    const fn = httpsCallable(functions, "getSiteCopy");
    const payload = { action: "faq" };
    if (surface) payload.surface = surface;
    const { data } = await fn(payload);
    const result = {
      items: Array.isArray(data?.items) ? data.items.map(normalizeFaq).filter(Boolean) : [],
      hidden_ids: Array.isArray(data?.hidden_ids) ? data.hidden_ids.map(String) : [],
    };
    if (key) writeFaqCmsCache(key, result);
    return result;
  } catch (err) {
    console.warn("[CookieCodeJar] FAQ CMS load failed:", err);
    return { items: [], hidden_ids: [] };
  }
}

export async function fetchLegalDoc(id) {
  if (!isConfigured || !functions) return null;
  try {
    const fn = httpsCallable(functions, "getSiteCopy");
    const { data } = await fn({ action: "legal", id });
    return data?.doc || null;
  } catch (err) {
    console.warn("[CookieCodeJar] Legal CMS load failed:", err);
    return null;
  }
}

/**
 * Seed FAQ as base; CMS rows override by id; hidden ids are removed.
 */
export function mergeFaqItems(surface, cmsItems, hiddenIds = []) {
  const hidden = new Set((hiddenIds || []).map(String));
  const seed = (surface === "landing" ? FAQ_SEED_LANDING : FAQ_SEED_PORTAL)
    .map(normalizeFaq)
    .filter(Boolean);
  const byId = new Map();

  for (const row of seed) {
    if (!row.id || hidden.has(row.id)) continue;
    byId.set(row.id, { ...row, admin_added: false, in_firestore: false });
  }

  for (const raw of cmsItems || []) {
    const row = normalizeFaq(raw);
    if (!row || row.surface !== surface || hidden.has(row.id)) continue;
    byId.set(row.id, { ...row, admin_added: true, in_firestore: true });
  }

  return [...byId.values()].sort(
    (a, b) => a.sort - b.sort || a.question.localeCompare(b.question)
  );
}

export function groupFaqBySection(items) {
  const order = [];
  const map = new Map();
  for (const item of items || []) {
    const key = item.section || "General";
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key).push(item);
  }
  return order.map((section) => ({ section, items: map.get(section) }));
}

function escapeHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function faqSectionId(section) {
  return `faq-${String(section || "general")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")}`;
}

export function renderFaqLayout(root, items, { footerHtml = "", jumpNav = false } = {}) {
  if (!root) return;
  const groups = groupFaqBySection(items);
  if (!groups.length) {
    root.innerHTML = `<p class="muted">No FAQ entries yet.</p>`;
    return;
  }

  const sectionsHtml = groups
    .map(
      (group) => `
      <section class="faq-section" id="${faqSectionId(group.section)}">
        <h2>${escapeHtml(group.section)}</h2>
        <div class="faq-list">
          ${group.items
            .map(
              (item) => `
            <details class="faq-item card">
              <summary>${escapeHtml(item.question)}</summary>
              <div class="faq-item__body">
                ${sanitizeFaqAnswerHtml(item.answer_html)}
              </div>
            </details>`
            )
            .join("")}
        </div>
      </section>`
    )
    .join("");

  if (!jumpNav) {
    root.innerHTML = `${sectionsHtml}${footerHtml || ""}`;
    return;
  }

  const jumpHtml = groups
    .map(
      (group) =>
        `<a class="faq-jump__chip" href="#${faqSectionId(group.section)}">${escapeHtml(group.section)}</a>`
    )
    .join("");

  root.innerHTML = `<div class="faq-shell">
      <nav class="faq-jump" aria-label="FAQ sections">${jumpHtml}</nav>
      <div class="faq-main">${sectionsHtml}${footerHtml || ""}</div>
    </div>`;
}

export async function loadAndRenderFaq(root, surface, options = {}) {
  const cms = await fetchFaqCms({ surface });
  const items = mergeFaqItems(surface, cms.items, cms.hidden_ids);
  renderFaqLayout(root, items, options);
  return items;
}

export { FAQ_SEED, FAQ_SEED_LANDING, FAQ_SEED_PORTAL };
